/* global Word, Office */

import * as React from "react";
import { ChatComposer } from "./chat/ChatComposer";
import { ChatKbContextModal } from "./chat/ChatKbContextModal";
import { ChatTranscript } from "./chat/ChatTranscript";
import "./chat/chat-ui.scss";
import { useChatBootstrap } from "../hooks/useChatBootstrap";
import { useAssistantAccess } from "../hooks/useAssistantAccess";
import { ASSISTANT_USE_DENIED } from "../api/contractHubApiErrors";
import { createChatSession, getAddInChatMessageHistory, patchAddInChatSuggestion, retryAddInChatMessage, sendAddInChatMessage } from "../api/chat";
import {
    ensureAssistantSession,
    isAssistantSseMessageId,
    streamAssistantChat,
    type AssistantChatContext,
} from "../api/assistant";
import { routeChatIntent } from "../chat/routeChatIntent";
import type { AddInHistoryMessage, AddInHistorySuggestion, ChatAvailableModel, ChatWordDiffOp, SaveAndInsertSuggestionArgs } from "../types/chat";
import { getAgreementIdFromDocument, getWordChanges } from "../agreement";

/**
 * Range-ref selection + insertion pattern — see Cenna/mock/cursorBasedApproach.md
 * (trackedObjects + Range ref; search only as fallback when the range expires).
 */

type ChatPanelProps = {
    /** When true, listens for document selection (Chat tab active). */
    active?: boolean;
};

type SelectedContext = {
    text: string;
    previewText: string;
};

const DEBOUNCE_MS = 300;
const MIN_SELECTION_LEN = 4;

function pickChatModelValue(models: ChatAvailableModel[], defaultId: string, previous: string): string {
    if (models.length === 0) {
        return "";
    }
    if (previous && models.some((m) => m.id === previous)) {
        return previous;
    }
    if (defaultId && models.some((m) => m.id === defaultId)) {
        return defaultId;
    }
    return models[0].id;
}

function sortMessagesChronological(items: AddInHistoryMessage[]): AddInHistoryMessage[] {
    return [...items].sort((a, b) => {
        const ta = new Date(a.createdAt).getTime();
        const tb = new Date(b.createdAt).getTime();
        if (Number.isNaN(ta) || Number.isNaN(tb)) {
            return 0;
        }
        return ta - tb;
    });
}

function mergeHistory(existing: AddInHistoryMessage[], incoming: AddInHistoryMessage[]): AddInHistoryMessage[] {
    const map = new Map<string, AddInHistoryMessage>();
    for (const m of existing) {
        if (m.id) {
            map.set(m.id, m);
        }
    }
    for (const m of incoming) {
        if (m.id) {
            map.set(m.id, m);
        }
    }
    return sortMessagesChronological(Array.from(map.values()));
}

function debounce(fn: () => void, ms: number): () => void {
    let t: ReturnType<typeof setTimeout> | null = null;
    return () => {
        if (t) {
            clearTimeout(t);
        }
        t = setTimeout(() => {
            t = null;
            fn();
        }, ms);
    };
}

function selectedTextForAssistantTurn(messages: AddInHistoryMessage[], assistantIdx: number): string {
    for (let i = assistantIdx - 1; i >= 0; i--) {
        if (messages[i].role === "user" && messages[i].selectedText.trim()) {
            return messages[i].selectedText.trim();
        }
    }
    return "";
}

function chatWordDiffFromTexts(sourceText: string, targetText: string): ChatWordDiffOp[] {
    return getWordChanges(sourceText, targetText).map((change) => ({
        op: change.type,
        text: change.text,
    }));
}

const ChatPanel: React.FC<ChatPanelProps> = ({ active = true }) => {
    /** Office.js proxies must live outside React state — see /docs/cursorBasedApproach.md */
    const trackedRangeRef = React.useRef<Word.Range | null>(null);
    const trackingContextRef = React.useRef<Word.RequestContext | null>(null);
    const composerInputRef = React.useRef<HTMLTextAreaElement | null>(null);

    const [selectedContext, setSelectedContext] = React.useState<SelectedContext | null>(null);
    const [inputValue, setInputValue] = React.useState("");
    const [chatModel, setChatModel] = React.useState<string>("");
    const assistantAccess = useAssistantAccess(active);
    const canUseAssistant = assistantAccess.status === "allowed";
    const accessLoading =
        assistantAccess.status === "idle" || assistantAccess.status === "loading";
    const accessDeniedMessage =
        assistantAccess.status === "denied"
            ? ASSISTANT_USE_DENIED
            : assistantAccess.status === "error"
              ? assistantAccess.message
              : null;
    /** Parallel bootstrap: default KB, models, sessions (add-in history loaded below). */
    const chatBootstrap = useChatBootstrap(active, canUseAssistant);
    const availableModels: ChatAvailableModel[] = chatBootstrap.models?.models ?? [];
    const modelsLoading = chatBootstrap.loading;
    const modelsError = chatBootstrap.modelsError;
    const modelsLoaded = chatBootstrap.loaded;
    /** Knowledge base file ids for `kb_selected` on add-in send (from `ChatKnowledgeBasePanel`). */
    const [kbSelectedFileIds, setKbSelectedFileIds] = React.useState<string[]>([]);
    /** File names for read-only context strip under the composer. */
    const [referenceKbFileLabels, setReferenceKbFileLabels] = React.useState<Array<{ id: string; fileName: string }>>([]);
    const [kbContextModalOpen, setKbContextModalOpen] = React.useState(false);
    const [sendBusy, setSendBusy] = React.useState(false);
    const [retryingAssistantId, setRetryingAssistantId] = React.useState<string | null>(null);
    const [sendError, setSendError] = React.useState<string | null>(null);
    const [historyMessages, setHistoryMessages] = React.useState<AddInHistoryMessage[]>([]);
    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [historyLoadingMore, setHistoryLoadingMore] = React.useState(false);
    const [historyExhausted, setHistoryExhausted] = React.useState(false);
    /** Next SQL ``offset`` when loading older messages (cumulative row skip). */
    const [nextOlderOffset, setNextOlderOffset] = React.useState(0);
    const [historyError, setHistoryError] = React.useState<string | null>(null);
    /** Session used for add-in rewrite sends — prefers bootstrap list, then a session created in this tab. */
    const chatSessionIdRef = React.useRef<string | null>(null);
    /** Abort in-flight Contract Hub assistant SSE turns. */
    const assistantAbortRef = React.useRef<AbortController | null>(null);
    /** Selection text captured when the user sends — used for the first Insert; then chain replaces prior insert. */
    const selectionSnapshotRef = React.useRef<string>("");
    /** After Insert, the text currently in the document from the last applied suggestion (chain). */
    const lastInsertedTextRef = React.useRef<string | null>(null);
    /** Latest selection text for search fallback (refs mirror tracked range; this is for UI + fallback). */
    const selectedTextRef = React.useRef<string>("");

    React.useEffect(() => {
        const data = chatBootstrap.models;
        if (!data) {
            return;
        }
        setChatModel((prev) => pickChatModelValue(data.models, data.defaultModel, prev));
    }, [chatBootstrap.models]);

    /** Seed session id from bootstrap only when unset — do not overwrite a session created in this tab. */
    React.useEffect(() => {
        if (chatBootstrap.activeSessionId && chatSessionIdRef.current === null) {
            chatSessionIdRef.current = chatBootstrap.activeSessionId;
        }
    }, [chatBootstrap.activeSessionId]);

    const refreshAddInHistory = React.useCallback(async (sessionId: string) => {
        setHistoryError(null);
        setHistoryLoading(true);
        setHistoryExhausted(false);
        setNextOlderOffset(0);
        try {
            const batch = await getAddInChatMessageHistory(sessionId, 20, 0);
            setHistoryMessages((prev) => {
                const sseOnly = prev.filter((m) => isAssistantSseMessageId(m.id));
                return sortMessagesChronological(mergeHistory(sseOnly, batch));
            });
            setNextOlderOffset(batch.length);
            setHistoryExhausted(batch.length === 0 || batch.length < 20);
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            setHistoryError(msg);
            setHistoryMessages((prev) => prev.filter((m) => isAssistantSseMessageId(m.id)));
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    const loadOlderAddInHistory = React.useCallback(async () => {
        const sid = chatSessionIdRef.current;
        if (!sid || historyLoadingMore || historyExhausted || historyLoading) {
            return;
        }
        setHistoryLoadingMore(true);
        setHistoryError(null);
        try {
            const batch = await getAddInChatMessageHistory(sid, 20, nextOlderOffset);
            if (batch.length === 0) {
                setHistoryExhausted(true);
                return;
            }
            setHistoryMessages((prev) => {
                const sseOnly = prev.filter((m) => isAssistantSseMessageId(m.id));
                const withoutSse = prev.filter((m) => !isAssistantSseMessageId(m.id));
                return sortMessagesChronological(mergeHistory([...sseOnly, ...withoutSse], batch));
            });
            setNextOlderOffset((o) => o + batch.length);
            if (batch.length < 20) {
                setHistoryExhausted(true);
            }
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            setHistoryError(msg);
        } finally {
            setHistoryLoadingMore(false);
        }
    }, [historyLoadingMore, historyExhausted, historyLoading, nextOlderOffset]);

    React.useEffect(() => {
        if (!active) {
            setHistoryMessages([]);
            setHistoryExhausted(false);
            setNextOlderOffset(0);
            setHistoryError(null);
            setHistoryLoading(false);
            setHistoryLoadingMore(false);
        }
    }, [active]);

    React.useEffect(() => {
        if (!active || !canUseAssistant || !chatBootstrap.loaded) {
            return;
        }
        const sid = chatSessionIdRef.current ?? chatBootstrap.activeSessionId;
        if (!sid) {
            return;
        }
        chatSessionIdRef.current = sid;
        void refreshAddInHistory(sid);
    }, [active, canUseAssistant, chatBootstrap.loaded, chatBootstrap.activeSessionId, refreshAddInHistory]);

    const releaseTrackedRangeOnly = React.useCallback(async () => {
        if (trackingContextRef.current && trackedRangeRef.current) {
            try {
                trackingContextRef.current.trackedObjects.remove(trackedRangeRef.current);
                await trackingContextRef.current.sync();
            } catch {
                /* ignore cleanup errors */
            }
        }
        trackedRangeRef.current = null;
        trackingContextRef.current = null;
    }, []);

    const clearSelectionCard = React.useCallback(async () => {
        await releaseTrackedRangeOnly();
        selectedTextRef.current = "";
        setSelectedContext(null);
    }, [releaseTrackedRangeOnly]);

    /** Finds `needleFull` (truncated for Word.search) in the document body. */
    const textExistsInDocument = React.useCallback(async (needleFull: string): Promise<boolean> => {
        const trimmed = needleFull.trim();
        if (!trimmed) {
            return false;
        }
        const needle = trimmed.length > 50 ? trimmed.slice(0, 50) : trimmed;
        try {
            let found = false;
            await Word.run(async (context) => {
                const results = context.document.body.search(needle, { matchCase: false });
                results.load("items");
                await context.sync();
                found = results.items.length > 0;
            });
            return found;
        } catch {
            return false;
        }
    }, []);

    const replaceTextInDocument = React.useCallback(async (needleFull: string, replacement: string): Promise<boolean> => {
        const trimmed = needleFull.trim();
        if (!trimmed) {
            return false;
        }
        const needle = trimmed.length > 50 ? trimmed.slice(0, 50) : trimmed;
        let replaced = false;
        try {
            await Word.run(async (context) => {
                const results = context.document.body.search(needle, { matchCase: false });
                results.load("items");
                await context.sync();
                if (results.items.length > 0) {
                    results.items[0].insertText(replacement, Word.InsertLocation.replace);
                    await context.sync();
                    replaced = true;
                }
            });
        } catch {
            /* ignore */
        }
        return replaced;
    }, []);

    function sanitizeDiffText(text: string): string {
        return text.replace(/\u0005/g, "").replace(/\r/g, "");
    }

    /** Word `Range.search` throws for long strings beyond this length. */
    const WORD_RANGE_SEARCH_MAX_LENGTH = 255;

    async function searchOrThrow(
        context: Word.RequestContext,
        cursor: Word.Range,
        end: Word.Range,
        searchText: string,
        errorLabel: string
    ): Promise<Word.Range> {
        const tail = cursor.expandTo(end);
        let found = tail.search(searchText, {
            matchCase: true,
            matchWholeWord: false,
            ignorePunct: false,
            ignoreSpace: false,
        });
        found.load("items");
        await context.sync();
        if (found.items.length === 0) {
            found = tail.search(searchText, {
                matchCase: false,
                matchWholeWord: false,
                ignorePunct: false,
                ignoreSpace: true,
            });
            found.load("items");
            await context.sync();
        }
        if (found.items.length === 0) {
            throw new Error(errorLabel);
        }
        return found.items[0];
    }

    async function searchOverlongRange(
        context: Word.RequestContext,
        cursor: Word.Range,
        end: Word.Range,
        segment: string,
        errorLabel: string
    ): Promise<Word.Range> {
        const prefixText = segment.slice(0, WORD_RANGE_SEARCH_MAX_LENGTH);
        const suffixText = segment.slice(-Math.min(200, WORD_RANGE_SEARCH_MAX_LENGTH));
        const prefixMatch = await searchOrThrow(context, cursor, end, prefixText, errorLabel);
        const suffixMatch = await searchOrThrow(
            context,
            prefixMatch.getRange(Word.RangeLocation.start),
            end,
            suffixText,
            errorLabel
        );
        return prefixMatch.expandTo(suffixMatch);
    }

    /**
     * Applies `word_diff` ops to a live Word.Range inside the SAME `Word.run` that owns `context`.
     * Mirrors `applyBodyParagraphWordChanges` in `agreement.ts`: `equal` advances a running cursor via
     * tail search, `deletion` removes the matched text (emitting a tracked revision when
     * `changeTrackingMode === trackAll`), and `insertion` inserts at the cursor.
     */
    async function applyWordDiffToRange(
        context: Word.RequestContext,
        range: Word.Range,
        ops: ChatWordDiffOp[]
    ): Promise<void> {
        const rangeEnd = range.getRange(Word.RangeLocation.end);
        let cursor = range.getRange(Word.RangeLocation.start);
        const hasMutations = ops.some((o) => o.op === "deletion" || o.op === "insertion");
        let appliedAnyChange = false;

        for (const raw of ops) {
            const segment = sanitizeDiffText(raw.text);
            const isOverlong = segment.length > WORD_RANGE_SEARCH_MAX_LENGTH;

            if (raw.op === "equal") {
                if (!segment) continue;
                const searchText = isOverlong ? segment.slice(-WORD_RANGE_SEARCH_MAX_LENGTH) : raw.text;
                const match = await searchOrThrow(
                    context,
                    cursor,
                    rangeEnd,
                    searchText,
                    "Could not match an equal segment from the word_diff. The selection may have changed."
                );
                cursor = match.getRange(Word.RangeLocation.end);
                continue;
            }

            if (raw.op === "deletion") {
                if (!segment) continue;
                const delRange = isOverlong
                    ? await searchOverlongRange(
                          context,
                          cursor,
                          rangeEnd,
                          segment,
                          "Could not locate text to delete. The selection may differ from the analyzed version."
                      )
                    : await searchOrThrow(
                          context,
                          cursor,
                          rangeEnd,
                          raw.text,
                          "Could not locate text to delete. The selection may differ from the analyzed version."
                      );
                delRange.insertText("", Word.InsertLocation.replace);
                await context.sync();
                cursor = delRange.getRange(Word.RangeLocation.start);
                appliedAnyChange = true;
                continue;
            }

            if (raw.op === "insertion") {
                if (!segment) continue;
                const inserted = cursor.insertText(segment, Word.InsertLocation.start);
                await context.sync();
                cursor = inserted.getRange(Word.RangeLocation.end);
                appliedAnyChange = true;
            }
        }

        if (hasMutations && !appliedAnyChange) {
            throw new Error("No edits were applied from word_diff. Try inserting the full suggestion text.");
        }
    }

    /** Locate document text matching the user's original selection snapshot (post-undo reinsert). */
    async function findBodyRangeForSnapshot(
        context: Word.RequestContext,
        snapshot: string
    ): Promise<Word.Range | null> {
        const trimmed = snapshot.trim();
        if (!trimmed) {
            return null;
        }
        if (trimmed.length <= WORD_RANGE_SEARCH_MAX_LENGTH) {
            const results = context.document.body.search(trimmed, { matchCase: false });
            results.load("items");
            await context.sync();
            return results.items[0] ?? null;
        }
        const startNeedle = trimmed.slice(0, WORD_RANGE_SEARCH_MAX_LENGTH);
        const endNeedle = trimmed.slice(-Math.min(200, WORD_RANGE_SEARCH_MAX_LENGTH));
        const startResults = context.document.body.search(startNeedle, { matchCase: false });
        startResults.load("items");
        await context.sync();
        if (startResults.items.length === 0) {
            return null;
        }
        const start = startResults.items[0];
        const bodyEnd = context.document.body.getRange(Word.RangeLocation.end);
        const endMatch = await searchOrThrow(
            context,
            start,
            bodyEnd,
            endNeedle,
            "Could not locate the full selected passage after undo. Reselect the text and try again."
        );
        return start.expandTo(endMatch);
    }

    /** Apply suggestion at the original selection via body search (when tracked range is gone). */
    async function applySuggestionAtSnapshot(
        snapshot: string,
        suggestion: AddInHistorySuggestion
    ): Promise<boolean> {
        const snap = snapshot.trim();
        if (!snap) {
            return false;
        }
        try {
            await Word.run(async (context) => {
                context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
                const range = await findBodyRangeForSnapshot(context, snap);
                if (!range) {
                    throw new Error("Could not find the original selected text in the document.");
                }
                if (suggestion.wordDiff && suggestion.wordDiff.length > 0) {
                    await applyWordDiffToRange(context, range, suggestion.wordDiff);
                } else {
                    range.insertText(suggestion.text, Word.InsertLocation.replace);
                    await context.sync();
                }
            });
            return true;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Snapshot-based suggestion insert failed", err);
            return false;
        }
    }

    /**
     * First Insert: replace/redline the user's original selection (tracked range or snapshot).
     *   - If `wordDiff` is available, applies granular ops with `changeTrackingMode = trackAll` so
     *     each deletion/insertion shows as a separate redline — matching the AI analysis flow in
     *     `agreement.ts`'s `applyBodyParagraphWordChanges`.
     *   - Otherwise falls back to a whole-range replace.
     * Later Inserts: replace the text introduced by the previous suggestion so each applies on top
     * of the last (word_diff was computed against the original selection, so we don't replay it).
     *
     * The tracked range is revived inside a fresh `Word.run(range, ...)` so that multi-op syncs
     * run against a live context rather than the stale one captured at selection time.
     */
    const insertSuggestionIntoDocument = React.useCallback(
        async (suggestion: AddInHistorySuggestion) => {
            const suggestionText = suggestion.text;
            let prev = lastInsertedTextRef.current;

            // After Undo, the chained text may be gone while the ref still points at it.
            if (prev) {
                const chainIntact = await textExistsInDocument(prev);
                if (!chainIntact) {
                    lastInsertedTextRef.current = null;
                    prev = null;
                }
            }

            if (prev) {
                const ok = await replaceTextInDocument(prev, suggestionText);
                if (ok) {
                    lastInsertedTextRef.current = suggestionText;
                    return;
                }
                lastInsertedTextRef.current = null;
            }

            if (trackedRangeRef.current) {
                const range = trackedRangeRef.current;
                let trackedDiffApplied = false;
                try {
                    await Word.run(range, async (context) => {
                        context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
                        if (suggestion.wordDiff && suggestion.wordDiff.length > 0) {
                            await applyWordDiffToRange(context, range, suggestion.wordDiff);
                        } else {
                            range.insertText(suggestionText, Word.InsertLocation.replace);
                            await context.sync();
                        }
                        context.trackedObjects.remove(range);
                        await context.sync();
                    });
                    trackedDiffApplied = true;
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn("Tracked-range word_diff insert failed; falling back to search", err);
                }

                if (trackedDiffApplied) {
                    trackedRangeRef.current = null;
                    trackingContextRef.current = null;
                    selectedTextRef.current = "";
                    setSelectedContext(null);
                    lastInsertedTextRef.current = suggestionText;
                    return;
                }
            }

            const snap = selectionSnapshotRef.current.trim();
            if (snap) {
                const applied = await applySuggestionAtSnapshot(snap, suggestion);
                if (applied) {
                    lastInsertedTextRef.current = suggestionText;
                    await clearSelectionCard();
                    return;
                }
            }

            if (snap) {
                const ok = await replaceTextInDocument(snap, suggestionText);
                if (ok) {
                    lastInsertedTextRef.current = suggestionText;
                    await clearSelectionCard();
                }
            }
        },
        [replaceTextInDocument, clearSelectionCard, textExistsInDocument]
    );

    const handleInsertSuggestion = React.useCallback(
        async (suggestion: AddInHistorySuggestion, _index: number) => {
            await insertSuggestionIntoDocument(suggestion);
        },
        [insertSuggestionIntoDocument]
    );

    const handleSaveAndInsertSuggestion = React.useCallback(
        async ({ messageId, messageIndex, suggestionIndex, editedText }: SaveAndInsertSuggestionArgs) => {
            const sessionId = chatSessionIdRef.current ?? chatBootstrap.activeSessionId;
            if (!sessionId) {
                throw new Error("No active chat session.");
            }

            setSendError(null);
            try {
                const updated = await patchAddInChatSuggestion(sessionId, messageId, suggestionIndex, editedText);

                setHistoryMessages((prev) =>
                    prev.map((row) => {
                        if (row.id !== messageId) {
                            return row;
                        }
                        const suggestions = [...row.suggestions];
                        if (suggestionIndex < 0 || suggestionIndex >= suggestions.length) {
                            return row;
                        }
                        suggestions[suggestionIndex] = {
                            ...suggestions[suggestionIndex],
                            text: updated.text,
                            ...(updated.rationale !== undefined ? { rationale: updated.rationale } : {}),
                            ...(updated.wordDiff ? { wordDiff: updated.wordDiff } : {}),
                        };
                        return { ...row, suggestions };
                    })
                );

                const currentSelection =
                    (selectedContext?.text ?? selectedTextRef.current ?? selectionSnapshotRef.current).trim() ||
                    selectedTextForAssistantTurn(historyMessages, messageIndex);

                const suggestionForInsert: AddInHistorySuggestion = {
                    text: editedText,
                    ...(updated.rationale ? { rationale: updated.rationale } : {}),
                    wordDiff: chatWordDiffFromTexts(currentSelection, editedText),
                };
                await insertSuggestionIntoDocument(suggestionForInsert);
            } catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                setSendError(msg);
                throw e;
            }
        },
        [
            chatBootstrap.activeSessionId,
            historyMessages,
            insertSuggestionIntoDocument,
            selectedContext?.text,
        ]
    );

    /** Word.run can hang; never block chat network on document property reads. */
    const readContractIdQuick = React.useCallback(async (): Promise<string | null> => {
        try {
            const id = await Promise.race([
                getAgreementIdFromDocument().then((v) => (v?.trim() ? v.trim() : null)),
                new Promise<null>((resolve) => {
                    window.setTimeout(() => resolve(null), 1500);
                }),
            ]);
            return id;
        } catch {
            return null;
        }
    }, []);

    /**
     * Hybrid send: rewrite/rephrase → ``/chat/add_in`` (suggestions + Insert);
     * hub Q&A / tools → ``/api/assistant/chat`` SSE.
     */
    const handleSendChat = React.useCallback(async (overrideQuery?: string) => {
        const query = (overrideQuery ?? inputValue).trim();
        if (!query || sendBusy || !canUseAssistant) {
            return;
        }
        selectionSnapshotRef.current = (selectedContext?.text ?? selectedTextRef.current ?? "").trim();
        lastInsertedTextRef.current = null;
        setSendError(null);
        setSendBusy(true);

        const selectedText =
            selectedContext?.text?.trim() || selectedTextRef.current.trim() || null;
        const path = routeChatIntent(query, selectedText);

        try {
            if (path === "assistant") {
                assistantAbortRef.current?.abort();
                const controller = new AbortController();
                assistantAbortRef.current = controller;

                // Optimistic rows first so the UI isn't stuck behind Word.run / session create.
                const turnId = `sse:${Date.now()}`;
                const placeholderSession = "pending";
                const userRow: AddInHistoryMessage = {
                    id: `${turnId}:user`,
                    sessionId: placeholderSession,
                    role: "user",
                    content: query,
                    selectedText: selectedText ?? "",
                    modelName: chatModel.trim() || "",
                    suggestions: [],
                    createdAt: new Date().toISOString(),
                };
                const assistantRow: AddInHistoryMessage = {
                    id: `${turnId}:assistant`,
                    sessionId: placeholderSession,
                    role: "assistant",
                    content: "",
                    selectedText: "",
                    modelName: chatModel.trim() || "",
                    suggestions: [],
                    createdAt: new Date().toISOString(),
                };
                setHistoryMessages((prev) => [...prev, userRow, assistantRow]);
                setInputValue("");

                // Don't await document props before hitting the network — Word.run can deadlock
                // the task pane and never reach /api/assistant/* (no server logs).
                const contract_id = await readContractIdQuick();
                const ctx: AssistantChatContext = {
                    view: "word",
                    ...(contract_id ? { contractId: contract_id } : {}),
                    ...(selectedText ? { selectedText } : {}),
                };

                const assistantSessionId = await ensureAssistantSession(ctx);
                setHistoryMessages((prev) =>
                    prev.map((m) =>
                        m.id === `${turnId}:user` || m.id === `${turnId}:assistant`
                            ? { ...m, sessionId: assistantSessionId }
                            : m
                    )
                );

                await streamAssistantChat({
                    prompt: query,
                    context: ctx,
                    sessionId: assistantSessionId,
                    model: chatModel.trim() || null,
                    signal: controller.signal,
                    handlers: {
                        onDelta: (text) => {
                            setHistoryMessages((prev) =>
                                prev.map((m) =>
                                    m.id === `${turnId}:assistant` ? { ...m, content: text } : m
                                )
                            );
                        },
                        onDone: (text) => {
                            setHistoryMessages((prev) =>
                                prev.map((m) =>
                                    m.id === `${turnId}:assistant` ? { ...m, content: text } : m
                                )
                            );
                        },
                        onError: (message) => {
                            setSendError(message);
                            setHistoryMessages((prev) =>
                                prev.map((m) =>
                                    m.id === `${turnId}:assistant`
                                        ? { ...m, content: m.content || `Error: ${message}` }
                                        : m
                                )
                            );
                        },
                    },
                });
                return;
            }

            // Same optimistic UX as assistant SSE: show the user turn immediately.
            // Server history replaces these pending rows on refresh.
            const turnId = `pending:${Date.now()}`;
            const placeholderSession = chatSessionIdRef.current ?? chatBootstrap.activeSessionId ?? "pending";
            setHistoryMessages((prev) => [
                ...prev,
                {
                    id: `${turnId}:user`,
                    sessionId: placeholderSession,
                    role: "user",
                    content: query,
                    selectedText: selectedText ?? "",
                    modelName: chatModel.trim() || "",
                    suggestions: [],
                    createdAt: new Date().toISOString(),
                },
                {
                    id: `${turnId}:assistant`,
                    sessionId: placeholderSession,
                    role: "assistant",
                    content: "",
                    selectedText: "",
                    modelName: chatModel.trim() || "",
                    suggestions: [],
                    createdAt: new Date().toISOString(),
                },
            ]);
            setInputValue("");

            const contract_id = await readContractIdQuick();
            let sessionId = chatSessionIdRef.current ?? chatBootstrap.activeSessionId;
            if (!sessionId) {
                const created = await createChatSession(
                    chatBootstrap.defaultKb?.id ? { knowledgeBaseId: chatBootstrap.defaultKb.id } : undefined
                );
                sessionId = created.id;
                chatSessionIdRef.current = sessionId;
            }
            await sendAddInChatMessage(sessionId, {
                selected_text: selectedText,
                user_query: query,
                kb_selected: kbSelectedFileIds.length > 0 ? kbSelectedFileIds : undefined,
                model: chatModel.trim() || undefined,
                contract_id,
            });
            await refreshAddInHistory(sessionId);
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                return;
            }
            const msg = e instanceof Error ? e.message : String(e);
            setSendError(msg);
            // Drop empty pending assistant placeholders; keep the optimistic user turn.
            setHistoryMessages((prev) =>
                prev.filter((m) => !(m.id.startsWith("pending:") && m.role === "assistant" && !m.content.trim()))
            );
        } finally {
            setSendBusy(false);
        }
    }, [
        inputValue,
        sendBusy,
        canUseAssistant,
        chatBootstrap.activeSessionId,
        chatBootstrap.defaultKb?.id,
        selectedContext?.text,
        kbSelectedFileIds,
        chatModel,
        refreshAddInHistory,
        readContractIdQuick,
    ]);

    const handleRetryAssistantMessage = React.useCallback(
        async (message: AddInHistoryMessage) => {
            if (isAssistantSseMessageId(message.id)) {
                return;
            }
            const sessionId = chatSessionIdRef.current ?? chatBootstrap.activeSessionId;
            if (!sessionId || sendBusy || message.role !== "assistant") {
                return;
            }
            setSendError(null);
            setSendBusy(true);
            setRetryingAssistantId(message.id);
            setHistoryMessages((prev) =>
                prev.map((m) =>
                    m.id === message.id ? { ...m, content: "", suggestions: [] } : m
                )
            );
            try {
                let contract_id: string | null = null;
                try {
                    const id = await getAgreementIdFromDocument();
                    contract_id = id?.trim() ? id.trim() : null;
                } catch {
                    contract_id = null;
                }
                await retryAddInChatMessage(sessionId, message.id, {
                    model: chatModel.trim() || undefined,
                    kb_selected: kbSelectedFileIds.length > 0 ? kbSelectedFileIds : undefined,
                    contract_id,
                });
                await refreshAddInHistory(sessionId);
            } catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                setSendError(msg);
                try {
                    await refreshAddInHistory(sessionId);
                } catch {
                    /* best-effort restore */
                }
            } finally {
                setSendBusy(false);
                setRetryingAssistantId(null);
            }
        },
        [
            sendBusy,
            chatBootstrap.activeSessionId,
            chatModel,
            kbSelectedFileIds,
            refreshAddInHistory,
        ]
    );

    const handleSelectionChange = React.useCallback(async () => {
        if (!active) {
            return;
        }

        await releaseTrackedRangeOnly();

        try {
            await Word.run(async (context) => {
                const selection = context.document.getSelection();
                selection.load("text");
                await context.sync();

                const text = (selection.text ?? "").trim();
                if (text.length < MIN_SELECTION_LEN) {
                    selectedTextRef.current = "";
                    setSelectedContext(null);
                    return;
                }

                context.trackedObjects.add(selection);
                await context.sync();

                trackedRangeRef.current = selection;
                trackingContextRef.current = context;
                selectedTextRef.current = text;

                setSelectedContext({
                    text,
                    previewText: text.length > 80 ? `${text.slice(0, 80)}…` : text,
                });
            });
        } catch {
            selectedTextRef.current = "";
            setSelectedContext(null);
        }
    }, [active, releaseTrackedRangeOnly]);

    React.useEffect(() => {
        if (!active) {
            void clearSelectionCard();
            return undefined;
        }

        const debounced = debounce(() => {
            void handleSelectionChange();
        }, DEBOUNCE_MS);

        const add = () => {
            try {
                Office.context.document.addHandlerAsync(
                    Office.EventType.DocumentSelectionChanged,
                    debounced,
                    () => { }
                );
            } catch {
                /* no-op */
            }
        };

        if (typeof Office !== "undefined" && Office.context?.document) {
            add();
        }

        void handleSelectionChange();

        return () => {
            try {
                Office.context.document.removeHandlerAsync(
                    Office.EventType.DocumentSelectionChanged,
                    { handler: debounced },
                    () => { }
                );
            } catch {
                /* no-op */
            }
            void clearSelectionCard();
        };
    }, [active, handleSelectionChange, clearSelectionCard]);

    return (
        <div className="chat-panel">
            <ChatTranscript
                messages={canUseAssistant ? historyMessages : []}
                historyLoading={canUseAssistant && historyLoading}
                historyLoadingMore={canUseAssistant && historyLoadingMore}
                historyExhausted={historyExhausted}
                onLoadOlder={loadOlderAddInHistory}
                loading={canUseAssistant && sendBusy}
                retryingAssistantId={retryingAssistantId}
                error={canUseAssistant ? sendError ?? historyError : null}
                onInsertSuggestion={handleInsertSuggestion}
                onSaveAndInsertSuggestion={handleSaveAndInsertSuggestion}
                onRetryAssistantMessage={handleRetryAssistantMessage}
                hasSelectedText={Boolean(selectedContext?.text?.trim())}
                onSuggestionClick={(text) => {
                    void handleSendChat(text);
                }}
                accessDeniedMessage={accessDeniedMessage}
                accessLoading={accessLoading}
            />

            <ChatComposer
                inputValue={inputValue}
                onInputChange={setInputValue}
                onSend={() => void handleSendChat()}
                sendBusy={sendBusy}
                selectedText={selectedContext?.text?.trim() ? selectedContext.text : null}
                onClearSelection={() => void clearSelectionCard()}
                referenceKbFileLabels={referenceKbFileLabels}
                onOpenKbContext={() => setKbContextModalOpen(true)}
                chatModel={chatModel}
                onChatModelChange={setChatModel}
                availableModels={availableModels}
                modelsLoading={modelsLoading}
                modelsLoaded={modelsLoaded}
                modelsError={modelsError}
                active={active}
                enabled={canUseAssistant}
                disabledReason={null}
                inputRef={composerInputRef}
            />

            <ChatKbContextModal
                open={kbContextModalOpen}
                onClose={() => setKbContextModalOpen(false)}
                active={active && kbContextModalOpen}
                onKbSelectedFileIdsChange={setKbSelectedFileIds}
                onReferenceFileLabelsChange={setReferenceKbFileLabels}
            />
        </div>
    );
};

export default ChatPanel;
